// src/admin/WhatsAppSettings.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Save, MessageCircle, AlertCircle, CheckCircle, Zap, Key,
  Phone, FileText, ToggleLeft, ToggleRight, Send, TestTube,
  ExternalLink, Copy, Shield
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import SuccessModal from '../components/SuccessModal';

const WhatsAppSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState('');

  const defaultConfig = {
    // Floating Button
    active: true,
    phone: '+92 300 1234567',
    message: 'Hello! I need help with my order.',
    bubbleText: 'Chat with us on WhatsApp!',
    position: 'bottom-right',

    // WhatsApp Cloud API (Order Confirmation)
    orderConfirmationEnabled: false,
    accessToken: '',
    phoneNumberId: '',
    templateName: 'order_confirmation',
    templateLanguage: 'en_US',
    businessNumber: '',
  };

  const [config, setConfig] = useState(defaultConfig);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const docRef = doc(db, 'settings', 'whatsapp');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setConfig({ ...defaultConfig, ...docSnap.data() });
        }
      } catch (error) {
        console.error('Error fetching config:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await setDoc(doc(db, 'settings', 'whatsapp'), config, { merge: true });
      setMessage({ type: 'success', text: 'WhatsApp settings saved!' });
      setShowSuccess(true);
      window.dispatchEvent(new Event('whatsappUpdated'));
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Save error:', error);
      setMessage({ type: 'error', text: 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestMessage = async () => {
    if (!testPhone) {
      setMessage({ type: 'error', text: 'Test phone number daalein.' });
      return;
    }

    if (!config.accessToken || !config.phoneNumberId) {
      setMessage({ type: 'error', text: 'Access Token aur Phone Number ID zaroori hain.' });
      return;
    }

    setTesting(true);
    setMessage({ type: '', text: '' });

    try {
      const cleanPhone = testPhone.replace(/[^0-9]/g, '');
      const response = await fetch(
        `https://graph.facebook.com/v20.0/${config.phoneNumberId}/messages`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${config.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            messaging_product: 'whatsapp',
            to: cleanPhone,
            type: 'text',
            text: {
              body: 'Test message from NAVIROSE ✅\nWhatsApp integration working!',
            },
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Test message bhej diya gaya! WhatsApp check karein.' });
      } else {
        setMessage({ type: 'error', text: data.error?.message || 'Test failed.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Test failed: ' + error.message });
    } finally {
      setTesting(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 1500);
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-pink-400 border-t-transparent"></div>
        <p className="mt-4 text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <FaWhatsapp className="text-green-600" size={28} /> WhatsApp Integration
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Floating button + Order Confirmation messages control karein.
        </p>
      </div>

      {message.text && (
        <div className={`text-sm p-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">

        {/* ================= 1. FLOATING BUTTON ================= */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <MessageCircle size={20} className="text-green-600" /> 1. Floating Chat Button
            </h2>
            <button
              type="button"
              onClick={() => setConfig({ ...config, active: !config.active })}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full font-semibold text-xs transition ${
                config.active
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {config.active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
              {config.active ? 'ACTIVE' : 'DISABLED'}
            </button>
          </div>

          {config.active && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  WhatsApp Number (with country code) *
                </label>
                <div className="relative">
                  <FaWhatsapp className="absolute left-3 top-3.5 text-gray-400" size={18} />
                  <input
                    type="tel"
                    value={config.phone}
                    onChange={(e) => setConfig({ ...config, phone: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 pl-11 pr-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="+92 300 1234567"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Pre-filled Message
                </label>
                <textarea
                  value={config.message}
                  onChange={(e) => setConfig({ ...config, message: e.target.value })}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm resize-none"
                  placeholder="Hello! I need help."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Bubble Text (Tooltip)
                </label>
                <input
                  type="text"
                  value={config.bubbleText}
                  onChange={(e) => setConfig({ ...config, bubbleText: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                  placeholder="Chat with us on WhatsApp!"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Position
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button"
                    onClick={() => setConfig({ ...config, position: 'bottom-left' })}
                    className={`p-3 rounded-lg border-2 font-semibold text-sm transition ${
                      config.position === 'bottom-left'
                        ? 'border-green-600 bg-green-50 text-green-800'
                        : 'border-gray-200 text-gray-600'
                    }`}>
                    Bottom Left
                  </button>
                  <button type="button"
                    onClick={() => setConfig({ ...config, position: 'bottom-right' })}
                    className={`p-3 rounded-lg border-2 font-semibold text-sm transition ${
                      config.position === 'bottom-right'
                        ? 'border-green-600 bg-green-50 text-green-800'
                        : 'border-gray-200 text-gray-600'
                    }`}>
                    Bottom Right
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ================= 2. ORDER CONFIRMATION API ================= */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Zap size={20} className="text-green-600" /> 2. Auto Order Confirmation
            </h2>
            <button
              type="button"
              onClick={() => setConfig({ ...config, orderConfirmationEnabled: !config.orderConfirmationEnabled })}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full font-semibold text-xs transition ${
                config.orderConfirmationEnabled
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {config.orderConfirmationEnabled ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
              {config.orderConfirmationEnabled ? 'ACTIVE' : 'DISABLED'}
            </button>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5">
            <p className="text-xs text-blue-800 leading-relaxed">
              <strong>ℹ️ Kaise Setup Karein:</strong><br />
              1. <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer" className="underline font-semibold">Meta Developer</a> par app banayein<br />
              2. WhatsApp product add karein<br />
              3. Access Token aur Phone Number ID copy karein<br />
              4. Neeche paste karein aur Save karein
            </p>
          </div>

          {config.orderConfirmationEnabled && (
            <div className="space-y-4">

              {/* Access Token */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Key size={14} /> Access Token (API Key) *
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowToken(!showToken)}
                    className="text-xs text-pink-600 font-semibold hover:underline"
                  >
                    {showToken ? 'Hide' : 'Show'}
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showToken ? 'text' : 'password'}
                    value={config.accessToken}
                    onChange={(e) => setConfig({ ...config, accessToken: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 pl-4 pr-12 focus:outline-none focus:border-green-600 text-sm font-mono"
                    placeholder="EAAG..."
                  />
                  {config.accessToken && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(config.accessToken, 'token')}
                      className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                    >
                      {copied === 'token' ? <CheckCircle size={18} className="text-green-600" /> : <Copy size={18} />}
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Meta Developer → WhatsApp → API Setup → Temporary/Permanent Token
                </p>
              </div>

              {/* Phone Number ID */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Phone size={14} /> Phone Number ID *
                </label>
                <input
                  type="text"
                  value={config.phoneNumberId}
                  onChange={(e) => setConfig({ ...config, phoneNumberId: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm font-mono"
                  placeholder="123456789012345"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Meta Developer → WhatsApp → API Setup → "From" number ka ID
                </p>
              </div>

              {/* Template Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <FileText size={14} /> Template Name *
                  </label>
                  <input
                    type="text"
                    value={config.templateName}
                    onChange={(e) => setConfig({ ...config, templateName: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm font-mono"
                    placeholder="order_confirmation"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Language Code
                  </label>
                  <select
                    value={config.templateLanguage}
                    onChange={(e) => setConfig({ ...config, templateLanguage: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm bg-white"
                  >
                    <option value="en_US">English (US)</option>
                    <option value="en_GB">English (UK)</option>
                    <option value="ur">Urdu</option>
                    <option value="hi">Hindi</option>
                  </select>
                </div>
              </div>

              {/* Test Section */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-5">
                <h3 className="text-sm font-bold text-yellow-800 mb-3 flex items-center gap-2">
                  <TestTube size={16} /> Test WhatsApp Message
                </h3>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-lg py-2.5 px-3 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="+92 300 1234567"
                  />
                  <button
                    type="button"
                    onClick={handleTestMessage}
                    disabled={testing}
                    className={`px-5 rounded-lg font-semibold text-sm btn-2d flex items-center gap-2 ${
                      testing ? 'opacity-70 cursor-not-allowed bg-gray-400' : 'bg-green-600 hover:bg-green-700'
                    } text-white`}
                  >
                    {testing ? (
                      <>
                        <span className="inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full anim-spin"></span>
                        Sending
                      </>
                    ) : (
                      <>
                        <Send size={14} /> Test
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-yellow-700 mt-2">
                  ⚠️ Test number "verified" hona chahiye Meta Console mein. Warna error aayega.
                </p>
              </div>

              {/* Template Preview */}
              <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-4">
                <h3 className="text-sm font-bold text-gray-700 mb-2">
                  Message Template Preview:
                </h3>
                <div className="bg-green-50 rounded-lg p-4 space-y-1">
                  <p className="text-sm text-gray-800">Hello <strong>{'{name}'}</strong>!</p>
                  <p className="text-sm text-gray-800 mt-2">Aapka order confirm ho gaya hai 🎉</p>
                  <p className="text-sm text-gray-800 mt-2">Order ID: <strong>{'{orderId}'}</strong></p>
                  <p className="text-sm text-gray-800">Total: <strong>Rs. {'{total}'}</strong></p>
                  <p className="text-sm text-gray-800 mt-2">Shukriya!<br />NAVIROSE</p>
                </div>
              </div>

            </div>
          )}
        </div>

        {/* SAVE */}
        <div className="flex justify-end sticky bottom-4">
          <button
            type="submit"
            disabled={saving}
            className={`text-white py-3 px-8 rounded-lg font-semibold transition flex items-center gap-2 shadow-xl ${
              saving ? 'opacity-70 cursor-not-allowed' : ''
            }`}
            style={{ backgroundColor: '#F5AEC0' }}
          >
            <Save size={18} /> {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

      </form>

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Success!"
        message="WhatsApp settings saved successfully!"
        buttonText="Great!"
      />

    </div>
  );
};

export default WhatsAppSettings;